import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  FC,
  ReactNode,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { useMutation, useQuery } from '@apollo/client';
import { GET_CURRENT_GUEST, UPDATE_GUEST } from './graphql';
import { usePostHog } from 'posthog-js/react';
import { authService } from '../services/auth';
import { LoginDialog } from '../components/Auth/LoginDialog';
import { SignupDialog } from '../components/Auth/SignupDialog';

interface Guest {
  id: number;
  email: string;
  unconfirmedEmail?: string;
  createdAt?: string;
  confirmed: boolean;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  phone?: string;
  gravatarUrl?: string;
  address?: {
    id: number;
    streetAddress1: string;
    streetAddress2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    formattedAddress?: string;
  };
}

interface UpdateCurrentGuestInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  image?: File | null;
  address: {
    streetAddress1: string;
    streetAddress2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    formattedAddress?: string;
  };
}

interface SignUpInput {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  password: string;
  passwordConfirmation: string;
}

interface AuthContextType {
  guest: Guest | null;
  isLoading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (input: SignUpInput) => Promise<void>;
  signOut: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (
    password: string,
    passwordConfirmation: string,
    token: string,
  ) => Promise<void>;
  resendConfirmation: (email: string) => Promise<void>;
  confirmEmail: (token: string) => Promise<void>;
  updateGuest: (input: UpdateCurrentGuestInput) => Promise<void>;
  signupDialogOpen: boolean;
  loginDialogOpen: boolean;
  setSignupDialogOpen: (open: boolean) => void;
  setLoginDialogOpen: (open: boolean) => void;
  refetch: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [searchParams, setSearchParams] = useSearchParams();
  const [guest, setGuest] = useState<Guest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signupDialogOpen, setSignupDialogOpen] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const { refetch, loading } = useQuery(GET_CURRENT_GUEST, {
    onCompleted: (data) => {
      setGuest(data.authenticatedGuest);
    },
    onError: (error) => {
      handleAuthError(error);
    },
  });

  const refetchGuest = async () => {
    const graphqlResponse = await refetch();
    setGuest(graphqlResponse.data.authenticatedGuest);
  };

  const [handleUpdateGuest] = useMutation(UPDATE_GUEST, {
    onCompleted: (data) => {
      if (data.updateCurrentGuest.guest) {
        setError(null);
        setGuest(data.updateCurrentGuest.guest);
        enqueueSnackbar('Profile updated', {
          variant: 'success',
        });
        if (data.updateCurrentGuest.guest.unconfirmedEmail) {
          enqueueSnackbar(
            `Please check your email to confirm your email address: ${data.updateCurrentGuest.guest.unconfirmedEmail}`,
            {
              variant: 'info',
            },
          );
        }
      }
      if (data.updateCurrentGuest.error) {
        handleAuthError(data.updateCurrentGuest.error);
      }
    },
    onError: (error) => {
      handleAuthError(error);
    },
  });

  const updateGuest = async (input: UpdateCurrentGuestInput) => {
    await handleUpdateGuest({ variables: { input } });
  };

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  useEffect(() => {
    if (guest) {
      posthog.identify(guest.id.toString(), {
        email: guest.email,
        first_name: guest.firstName,
        last_name: guest.lastName,
      });
    }
  }, [guest, posthog]);

  useEffect(() => {
    const guestAuthParam = searchParams.get('guest_auth');

    if (guestAuthParam && !guest) {
      searchParams.delete('guest_auth');
      setSearchParams(searchParams);

      if (guestAuthParam === 'login') {
        setSignupDialogOpen(false);
        setLoginDialogOpen(true);
      } else if (guestAuthParam === 'signup') {
        setLoginDialogOpen(false);
        setSignupDialogOpen(true);
      }
    }
  }, [searchParams, setSearchParams, guest]);

  const handleAuthError = (err: unknown) => {
    const message = err instanceof Error ? err.message : 'An error occurred';
    setError(message);
    enqueueSnackbar(message, {
      variant: 'error',
    });
  };

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      const response = await authService.signIn({ email, password });
      if (response.status.code === 200 || response.status.code === 302) {
        await refetchGuest();
        enqueueSnackbar('Logged in successfully', {
          variant: 'success',
        });
      } else {
        handleAuthError(response.status.message);
      }
    } catch (err) {
      handleAuthError(err);
    }
  };

  const signUp = async (input: SignUpInput) => {
    const {
      email,
      password,
      passwordConfirmation,
      firstName,
      lastName,
      phone,
    } = input;
    try {
      setError(null);
      await authService.signUp({
        email,
        password,
        password_confirmation: passwordConfirmation,
        first_name: firstName,
        last_name: lastName,
        phone,
      });

      const graphqlResponse = await refetch();
      setGuest(graphqlResponse.data.authenticatedGuest);
      enqueueSnackbar(
        'Signed up successfully. Please check your email to confirm your account.',
        {
          variant: 'success',
        },
      );
    } catch (err) {
      handleAuthError(err);
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      await authService.signOut();
      setGuest(null);
      enqueueSnackbar('Signed out successfully', {
        variant: 'success',
      });
    } catch (err) {
      handleAuthError(err);
    }
  };

  const requestPasswordReset = async (email: string) => {
    try {
      setError(null);
      await authService.requestPasswordReset({ email });
    } catch (err) {
      handleAuthError(err);
    }
  };

  const resetPassword = async (
    password: string,
    passwordConfirmation: string,
    token: string,
  ) => {
    try {
      setError(null);
      await authService.resetPassword({
        password,
        password_confirmation: passwordConfirmation,
        reset_password_token: token,
      });
      enqueueSnackbar(
        'Password reset successfully. Please sign in to continue.',
        {
          variant: 'success',
        },
      );
      navigate('/?guest_auth=login');
    } catch (err) {
      handleAuthError(err);
    }
  };

  const resendConfirmation = async (email: string) => {
    try {
      setError(null);
      await authService.resendConfirmation({ email });
    } catch (err) {
      handleAuthError(err);
    }
  };

  const confirmEmail = async (token: string) => {
    try {
      setError(null);
      const response = await authService.confirmEmail(token);
      if (response.data) {
        await refetchGuest();
      }
      enqueueSnackbar('Email confirmed successfully', {
        variant: 'success',
      });
    } catch (err) {
      handleAuthError(err);
    }
  };

  const value = {
    guest,
    isLoading,
    error,
    setError,
    signIn,
    signUp,
    signOut,
    requestPasswordReset,
    resetPassword,
    resendConfirmation,
    confirmEmail,
    updateGuest,
    signupDialogOpen,
    loginDialogOpen,
    setSignupDialogOpen,
    setLoginDialogOpen,
    refetch: refetchGuest,
  };

  return <AuthContext.Provider value={value}>
    {children}
            <LoginDialog />
        <SignupDialog />
    </AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
